import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
} from '@mui/material';
import {
  Activity,
  Bot,
  Code2,
  Coins,
  Edit,
  KeyRound,
  MessageSquare,
  Package,
  Trash2,
} from 'lucide-react';
import { UserStatusBadge, RoleBadge, PlanBadge } from './UserStatusBadge';
import { UserFormModal } from './UserFormModal';
import { useAdjustUserTokens, useUpdateUserStatus } from '../hooks/useUsers';
import type {
  AdjustUserTokenInput,
  RegistrationClient,
  RegistrationProvider,
  User,
  UserSortBy,
  UserSortOrder,
} from '../types';
import { useNotification } from '../../../../hooks/useNotification';
import { FormattedNumberTextField } from '../../../../components/common/FormattedNumberTextField';
import { formatAdminNumber } from '../../../../utils/numberFormat';

interface UserTableProps {
  users: User[];
  loading: boolean;
  page: number;
  rowsPerPage: number;
  totalUsers: number;
  sortBy: UserSortBy | '';
  sortOrder: UserSortOrder;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
  onSortChange: (column: UserSortBy) => void;
  onUserClick?: (user: User) => void;
  onCredentialsClick?: (user: User, tab: 'apiKeys' | 'widgets') => void;
  onAdvanceTokenLedgerClick?: (user: User, direction: 'credit' | 'debit') => void;
}

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmText: string;
  confirmColor?: 'error' | 'primary';
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmText,
  confirmColor = 'primary',
  loading,
  onConfirm,
  onCancel,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      PaperProps={{
        sx: {
          bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgb(30, 41, 59)' : 'white'),
          color: (theme) => (theme.palette.mode === 'dark' ? 'white' : 'rgb(17, 24, 39)'),
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <p className="text-gray-700 dark:text-slate-300">{message}</p>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onCancel}
          disabled={loading}
          sx={{
            color: (theme) =>
              theme.palette.mode === 'dark' ? 'rgb(148, 163, 184)' : 'rgb(107, 114, 128)',
            '&:hover': {
              bgcolor: (theme) =>
                theme.palette.mode === 'dark' ? 'rgb(51, 65, 85)' : 'rgb(243, 244, 246)',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          disabled={loading}
          variant="contained"
          color={confirmColor}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Processing...' : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const registrationProviderLabels: Record<RegistrationProvider, string> = {
  EMAIL: 'Email',
  GOOGLE: 'Google',
  FACEBOOK: 'Facebook',
  APPLE: 'Apple',
  GITHUB: 'GitHub',
  ANONYMOUS: 'Anonymous',
  ADMIN_CREATED: 'Admin',
  UNKNOWN: 'Unknown',
};

const registrationClientLabels: Record<RegistrationClient, string> = {
  WEB: 'Web',
  IOS: 'iOS',
  ANDROID: 'Android',
  ADMIN: 'Admin',
  API: 'API',
  UNKNOWN: 'Unknown',
};

const registrationProviderBadgeClasses: Record<RegistrationProvider, string> = {
  EMAIL: 'bg-blue-100 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300',
  GOOGLE: 'bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-300',
  FACEBOOK: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300',
  APPLE: 'bg-slate-200 text-slate-800 dark:bg-slate-600 dark:text-slate-100',
  GITHUB: 'bg-neutral-200 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-100',
  ANONYMOUS: 'bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300',
  ADMIN_CREATED: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300',
  UNKNOWN: 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-slate-300',
};

const normalizeRegistrationProvider = (user: User): RegistrationProvider => {
  if (user.registrationProvider) {
    return user.registrationProvider;
  }

  return user.authProvider === 'NORMAL' || user.authProvider === 'EMAIL'
    ? 'EMAIL'
    : user.authProvider;
};

export const UserTable: React.FC<UserTableProps> = ({
  users,
  loading,
  page,
  rowsPerPage,
  totalUsers,
  sortBy,
  sortOrder,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  onUserClick,
  onCredentialsClick,
  onAdvanceTokenLedgerClick,
}) => {
  const { success, error: notifyError } = useNotification();
  const updateStatusMutation = useUpdateUserStatus();
  const adjustTokensMutation = useAdjustUserTokens();

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | undefined>();
  const [tokenDialogUser, setTokenDialogUser] = useState<User | null>(null);
  const [tokenForm, setTokenForm] = useState<AdjustUserTokenInput>({
    amount: 10000,
    direction: 'credit',
    reason: 'admin_grant',
    note: '',
  });

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    user: User | null;
    action: 'suspend' | 'activate';
  }>({
    open: false,
    user: null,
    action: 'suspend',
  });

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setEditModalOpen(true);
  };

  const handleStatusToggle = (user: User) => {
    setConfirmDialog({
      open: true,
      user,
      action: user.isActive ? 'suspend' : 'activate',
    });
  };

  const handleOpenTokenDialog = (user: User) => {
    setTokenDialogUser(user);
    setTokenForm({
      amount: 10000,
      direction: 'credit',
      reason: 'admin_grant',
      note: '',
    });
  };

  const handleConfirmStatusToggle = async () => {
    if (!confirmDialog.user) return;

    try {
      await updateStatusMutation.mutateAsync({
        publicId: confirmDialog.user.publicId,
        data: { isActive: !confirmDialog.user.isActive },
      });

      success(
        `User ${confirmDialog.action === 'suspend' ? 'suspended' : 'activated'} successfully.`
      );
      setConfirmDialog({ open: false, user: null, action: 'suspend' });
    } catch (error) {
      console.error('Status update error:', error);
    }
  };

  const handleCancelConfirm = () => {
    setConfirmDialog({ open: false, user: null, action: 'suspend' });
  };

  const handleAdjustTokens = async () => {
    if (!tokenDialogUser) return;

    if (!Number.isFinite(tokenForm.amount) || tokenForm.amount <= 0) {
      notifyError('Credit amount must be greater than 0.');
      return;
    }

    try {
      await adjustTokensMutation.mutateAsync({
        publicId: tokenDialogUser.publicId,
        data: {
          ...tokenForm,
          note: tokenForm.note?.trim() || undefined,
        },
      });

      success('Advance credits updated successfully.');
      setTokenDialogUser(null);
    } catch (error) {
      console.error('Advance token adjustment error:', error);
      notifyError(error instanceof Error ? error.message : 'Failed to adjust advance credits.');
    }
  };

  const formatTokens = (tokens: number) => formatAdminNumber(tokens);

  const getSortDirection = (column: UserSortBy): UserSortOrder =>
    sortBy === column ? sortOrder : 'desc';

  const sortableHeaderSx = {
    color: 'inherit',
    '&.Mui-active': {
      color: 'inherit',
    },
    '& .MuiTableSortLabel-icon': {
      color: 'inherit !important',
    },
  };

  const formatWalletSourceSummary = (user: User) => {
    const summary = user.walletSourceSummary;

    if (!summary) {
      return 'No advance credit ledger entries yet.';
    }

    return [
      `Purchased: ${formatTokens(summary.purchased)}`,
      `Admin granted: ${formatTokens(summary.adminGranted)}`,
      `Carry-over: ${formatTokens(summary.carryOver)}`,
      `Refund/adjustment: ${formatTokens(summary.refundAdjustment)}`,
      `Debited: ${formatTokens(summary.debited)}`,
      `Other credits: ${formatTokens(summary.otherCredits)}`,
    ].join('\n');
  };

  const getAdvanceTokensGranted = ({ walletSourceSummary: summary }: User) =>
    summary
      ? (summary.purchased ?? 0) +
        (summary.adminGranted ?? 0) +
        (summary.carryOver ?? 0) +
        (summary.otherCredits ?? 0)
      : 0;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <CircularProgress sx={{ color: '#3B82F6' }} />
      </div>
    );
  }

  if (!loading && users.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-12 text-center dark:border-slate-700 dark:bg-slate-800">
        <p className="text-lg text-gray-600 dark:text-slate-400">No users found</p>
        <p className="mt-2 text-sm text-gray-500 dark:text-slate-500">
          Try adjusting your search or filters
        </p>
      </div>
    );
  }

  return (
    <>
      <TableContainer
        sx={{
          width: '100%',
          maxWidth: '100%',
          overflowX: 'auto',
          bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgb(30, 41, 59)' : 'white'),
          borderRadius: 2,
          border: (theme) =>
            theme.palette.mode === 'dark'
              ? '1px solid rgb(51, 65, 85)'
              : '1px solid rgb(229, 231, 235)',
        }}
      >
        <Table sx={{ minWidth: 1970, tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow
              sx={{
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark' ? 'rgb(15, 23, 42)' : 'rgb(249, 250, 251)',
              }}
            >
              <TableCell
                sx={{
                  width: 180,
                  color: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgb(148, 163, 184)' : 'rgb(55, 65, 81)',
                  fontWeight: 600,
                }}
              >
                Username
              </TableCell>
              <TableCell
                sx={{
                  width: 260,
                  color: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgb(148, 163, 184)' : 'rgb(55, 65, 81)',
                  fontWeight: 600,
                }}
              >
                Email
              </TableCell>
              <TableCell
                sx={{
                  width: 100,
                  color: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgb(148, 163, 184)' : 'rgb(55, 65, 81)',
                  fontWeight: 600,
                }}
              >
                Role
              </TableCell>
              <TableCell
                sx={{
                  width: 160,
                  color: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgb(148, 163, 184)' : 'rgb(55, 65, 81)',
                  fontWeight: 600,
                }}
              >
                Registration
              </TableCell>
              <TableCell
                sx={{
                  width: 130,
                  color: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgb(148, 163, 184)' : 'rgb(55, 65, 81)',
                  fontWeight: 600,
                }}
              >
                Plan
              </TableCell>
              <TableCell
                sx={{
                  width: 150,
                  color: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgb(148, 163, 184)' : 'rgb(55, 65, 81)',
                  fontWeight: 600,
                }}
                sortDirection={sortBy === 'walletTokens' ? sortOrder : false}
              >
                <TableSortLabel
                  active={sortBy === 'walletTokens'}
                  direction={getSortDirection('walletTokens')}
                  onClick={() => onSortChange('walletTokens')}
                  sx={sortableHeaderSx}
                >
                  Advance Credits
                </TableSortLabel>
              </TableCell>
              <TableCell
                sx={{
                  width: 150,
                  color: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgb(148, 163, 184)' : 'rgb(55, 65, 81)',
                  fontWeight: 600,
                }}
              >
                Package Credits Used
              </TableCell>
              <TableCell
                sx={{
                  width: 150,
                  color: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgb(148, 163, 184)' : 'rgb(55, 65, 81)',
                  fontWeight: 600,
                }}
              >
                Advance Credits Used
              </TableCell>
              <TableCell
                sx={{
                  width: 140,
                  color: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgb(148, 163, 184)' : 'rgb(55, 65, 81)',
                  fontWeight: 600,
                }}
                sortDirection={sortBy === 'chatSessionCount' ? sortOrder : false}
              >
                <TableSortLabel
                  active={sortBy === 'chatSessionCount'}
                  direction={getSortDirection('chatSessionCount')}
                  onClick={() => onSortChange('chatSessionCount')}
                  sx={sortableHeaderSx}
                >
                  Chat Sessions
                </TableSortLabel>
              </TableCell>
              <TableCell
                sx={{
                  width: 120,
                  color: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgb(148, 163, 184)' : 'rgb(55, 65, 81)',
                  fontWeight: 600,
                }}
                sortDirection={sortBy === 'agentCount' ? sortOrder : false}
              >
                <TableSortLabel
                  active={sortBy === 'agentCount'}
                  direction={getSortDirection('agentCount')}
                  onClick={() => onSortChange('agentCount')}
                  sx={sortableHeaderSx}
                >
                  Agents
                </TableSortLabel>
              </TableCell>
              <TableCell
                sx={{
                  width: 120,
                  color: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgb(148, 163, 184)' : 'rgb(55, 65, 81)',
                  fontWeight: 600,
                }}
                sortDirection={sortBy === 'apiKeyCount' ? sortOrder : false}
              >
                <TableSortLabel
                  active={sortBy === 'apiKeyCount'}
                  direction={getSortDirection('apiKeyCount')}
                  onClick={() => onSortChange('apiKeyCount')}
                  sx={sortableHeaderSx}
                >
                  API Keys
                </TableSortLabel>
              </TableCell>
              <TableCell
                sx={{
                  width: 120,
                  color: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgb(148, 163, 184)' : 'rgb(55, 65, 81)',
                  fontWeight: 600,
                }}
                sortDirection={sortBy === 'embedWidgetCount' ? sortOrder : false}
              >
                <TableSortLabel
                  active={sortBy === 'embedWidgetCount'}
                  direction={getSortDirection('embedWidgetCount')}
                  onClick={() => onSortChange('embedWidgetCount')}
                  sx={sortableHeaderSx}
                >
                  Widgets
                </TableSortLabel>
              </TableCell>
              <TableCell
                sx={{
                  width: 130,
                  color: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgb(148, 163, 184)' : 'rgb(55, 65, 81)',
                  fontWeight: 600,
                }}
              >
                Status
              </TableCell>
              <TableCell
                sx={{
                  width: 120,
                  color: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgb(148, 163, 184)' : 'rgb(55, 65, 81)',
                  fontWeight: 600,
                }}
              >
                Joined
              </TableCell>
              <TableCell
                sx={{
                  width: 140,
                  color: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgb(148, 163, 184)' : 'rgb(55, 65, 81)',
                  fontWeight: 600,
                }}
                align="right"
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {users.map((user) => (
              <TableRow
                key={user.publicId}
                onClick={() => onUserClick?.(user)}
                sx={{
                  cursor: onUserClick ? 'pointer' : 'default',
                  '&:hover': {
                    bgcolor: (theme) =>
                      theme.palette.mode === 'dark' ? 'rgb(51, 65, 85)' : 'rgb(249, 250, 251)',
                  },
                  borderBottom: (theme) =>
                    theme.palette.mode === 'dark'
                      ? '1px solid rgb(51, 65, 85)'
                      : '1px solid rgb(229, 231, 235)',
                }}
              >
                <TableCell
                  sx={{
                    color: (theme) => (theme.palette.mode === 'dark' ? 'white' : 'rgb(17, 24, 39)'),
                  }}
                >
                  <span className="block truncate">
                    {user.userName || (
                      <span className="text-gray-400 dark:text-slate-500">N/A</span>
                    )}
                  </span>
                </TableCell>
                <TableCell
                  sx={{
                    color: (theme) =>
                      theme.palette.mode === 'dark' ? 'rgb(148, 163, 184)' : 'rgb(107, 114, 128)',
                  }}
                >
                  <span className="block truncate">{user.email}</span>
                </TableCell>
                <TableCell>
                  <RoleBadge role={user.role} />
                </TableCell>
                <TableCell>
                  <div className="flex min-w-0 flex-col items-start gap-1">
                    <span
                      className={`inline-flex max-w-full items-center truncate rounded-full px-2.5 py-1 text-xs font-semibold ${
                        registrationProviderBadgeClasses[normalizeRegistrationProvider(user)]
                      }`}
                    >
                      {registrationProviderLabels[normalizeRegistrationProvider(user)]}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-slate-400">
                      {registrationClientLabels[user.registrationClient || 'UNKNOWN']}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <PlanBadge plan={user.plan} />
                </TableCell>
                <TableCell
                  onClick={(event) => event.stopPropagation()}
                  sx={{
                    color: (theme) =>
                      theme.palette.mode === 'dark' ? 'rgb(226, 232, 240)' : 'rgb(31, 41, 55)',
                  }}
                >
                  <Tooltip
                    title={
                      <span className="flex flex-col gap-1">
                        <span style={{ whiteSpace: 'pre-line' }}>
                          {formatWalletSourceSummary(user)}
                        </span>
                        <span
                          className={
                            (user.walletTokens ?? 0) < 0
                              ? 'font-semibold text-amber-300'
                              : 'text-slate-100'
                          }
                        >
                          Remaining: {formatTokens(user.walletTokens ?? 0)}
                        </span>
                      </span>
                    }
                    placement="top"
                  >
                    <button
                      type="button"
                      onClick={() => onAdvanceTokenLedgerClick?.(user, 'credit')}
                      className="inline-flex max-w-full items-center gap-1 truncate rounded-md px-1.5 py-1 font-medium hover:bg-amber-50 dark:hover:bg-amber-500/10"
                    >
                      <Coins size={15} className="shrink-0 text-amber-500" />
                      {formatTokens(getAdvanceTokensGranted(user))}
                    </button>
                  </Tooltip>
                </TableCell>
                <TableCell
                  sx={{
                    color: (theme) =>
                      theme.palette.mode === 'dark' ? 'rgb(226, 232, 240)' : 'rgb(31, 41, 55)',
                  }}
                >
                  <Tooltip title="Credits debited from the user's package quota">
                    <span className="inline-flex max-w-full cursor-help items-center gap-1 truncate font-medium">
                      <Package size={15} className="shrink-0 text-indigo-500" />
                      {formatTokens(user.packageTokensUsed ?? 0)}
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell
                  onClick={(event) => event.stopPropagation()}
                  sx={{
                    color: (theme) =>
                      theme.palette.mode === 'dark' ? 'rgb(226, 232, 240)' : 'rgb(31, 41, 55)',
                  }}
                >
                  <Tooltip title="Lifetime advance credits debited">
                    <button
                      type="button"
                      onClick={() => onAdvanceTokenLedgerClick?.(user, 'debit')}
                      className="inline-flex max-w-full items-center gap-1 truncate rounded-md px-1.5 py-1 font-medium hover:bg-sky-50 dark:hover:bg-sky-500/10"
                    >
                      <Activity size={15} className="shrink-0 text-sky-500" />
                      {formatTokens(user.advanceTokensUsed ?? 0)}
                    </button>
                  </Tooltip>
                </TableCell>
                <TableCell
                  sx={{
                    color: (theme) =>
                      theme.palette.mode === 'dark' ? 'rgb(226, 232, 240)' : 'rgb(31, 41, 55)',
                  }}
                >
                  <Tooltip title="Total chat sessions created by this user">
                    <span className="inline-flex max-w-full cursor-help items-center gap-1 truncate font-medium">
                      <MessageSquare size={15} className="shrink-0 text-emerald-500" />
                      {formatAdminNumber(user.chatSessionCount ?? 0)}
                    </span>
                  </Tooltip>
                </TableCell>

                <TableCell
                  sx={{
                    color: (theme) =>
                      theme.palette.mode === 'dark' ? 'rgb(226, 232, 240)' : 'rgb(31, 41, 55)',
                  }}
                >
                  <Tooltip title="Total user-owned agents created by this user">
                    <span className="inline-flex max-w-full cursor-help items-center gap-1 truncate font-medium">
                      <Bot size={15} className="shrink-0 text-violet-500" />
                      {formatAdminNumber(user.agentCount ?? 0)}
                    </span>
                  </Tooltip>
                </TableCell>

                <TableCell
                  onClick={(event) => event.stopPropagation()}
                  sx={{
                    color: (theme) =>
                      theme.palette.mode === 'dark' ? 'rgb(226, 232, 240)' : 'rgb(31, 41, 55)',
                  }}
                >
                  <Tooltip title="Active API keys / total API keys">
                    <button
                      type="button"
                      onClick={() => onCredentialsClick?.(user, 'apiKeys')}
                      className="inline-flex max-w-full items-center gap-1 truncate rounded-md px-1.5 py-1 font-medium hover:bg-blue-50 dark:hover:bg-blue-500/10"
                    >
                      <KeyRound size={15} className="shrink-0 text-blue-500" />
                      {formatAdminNumber(user.activeApiKeyCount ?? 0)}/
                      {formatAdminNumber(user.apiKeyCount ?? 0)}
                    </button>
                  </Tooltip>
                </TableCell>

                <TableCell
                  onClick={(event) => event.stopPropagation()}
                  sx={{
                    color: (theme) =>
                      theme.palette.mode === 'dark' ? 'rgb(226, 232, 240)' : 'rgb(31, 41, 55)',
                  }}
                >
                  <Tooltip title="Active embed widgets / total embed widgets">
                    <button
                      type="button"
                      onClick={() => onCredentialsClick?.(user, 'widgets')}
                      className="inline-flex max-w-full items-center gap-1 truncate rounded-md px-1.5 py-1 font-medium hover:bg-teal-50 dark:hover:bg-teal-500/10"
                    >
                      <Code2 size={15} className="shrink-0 text-teal-500" />
                      {formatAdminNumber(user.activeEmbedWidgetCount ?? 0)}/
                      {formatAdminNumber(user.embedWidgetCount ?? 0)}
                    </button>
                  </Tooltip>
                </TableCell>

                <TableCell>
                  <UserStatusBadge isActive={user.isActive} />
                </TableCell>

                <TableCell
                  sx={{
                    color: (theme) =>
                      theme.palette.mode === 'dark' ? 'rgb(148, 163, 184)' : 'rgb(107, 114, 128)',
                  }}
                >
                  {formatDate(user.createdAt)}
                </TableCell>

                <TableCell align="right" onClick={(event) => event.stopPropagation()}>
                  <div className="flex justify-end gap-1">
                    <Tooltip title="Edit user">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(user)}
                        sx={{
                          color: '#3B82F6',
                          '&:hover': { bgcolor: 'rgba(59, 130, 246, 0.1)' },
                        }}
                      >
                        <Edit size={18} />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Add or debit advance credits">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenTokenDialog(user)}
                        sx={{
                          color: '#f59e0b',
                          '&:hover': { bgcolor: 'rgba(245, 158, 11, 0.1)' },
                        }}
                      >
                        <Coins size={18} />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title={user.isActive ? 'Suspend user' : 'Activate user'}>
                      <IconButton
                        size="small"
                        onClick={() => handleStatusToggle(user)}
                        sx={{
                          color: user.isActive ? '#ef4444' : '#22c55e',
                          '&:hover': {
                            bgcolor: user.isActive
                              ? 'rgba(239, 68, 68, 0.1)'
                              : 'rgba(34, 197, 94, 0.1)',
                          },
                        }}
                      >
                        <Trash2 size={18} />
                      </IconButton>
                    </Tooltip>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={totalUsers}
          page={page}
          onPageChange={(_, newPage) => onPageChange(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{
            color: (theme) =>
              theme.palette.mode === 'dark' ? 'rgb(148, 163, 184)' : 'rgb(107, 114, 128)',
            borderTop: (theme) =>
              theme.palette.mode === 'dark'
                ? '1px solid rgb(51, 65, 85)'
                : '1px solid rgb(229, 231, 235)',
            '& .MuiTablePagination-selectIcon': {
              color: (theme) =>
                theme.palette.mode === 'dark' ? 'rgb(148, 163, 184)' : 'rgb(107, 114, 128)',
            },
            '& .MuiTablePagination-actions button': {
              color: (theme) =>
                theme.palette.mode === 'dark' ? 'rgb(148, 163, 184)' : 'rgb(107, 114, 128)',
              '&:hover': {
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark' ? 'rgb(51, 65, 85)' : 'rgb(243, 244, 246)',
              },
              '&.Mui-disabled': {
                color: (theme) =>
                  theme.palette.mode === 'dark' ? 'rgb(71, 85, 105)' : 'rgb(209, 213, 219)',
              },
            },
          }}
        />
      </TableContainer>

      <UserFormModal
        open={editModalOpen}
        mode="edit"
        user={selectedUser}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedUser(undefined);
        }}
      />

      <ConfirmDialog
        open={confirmDialog.open}
        title={`${confirmDialog.action === 'suspend' ? 'Suspend' : 'Activate'} User`}
        message={`Are you sure you want to ${confirmDialog.action === 'suspend' ? 'suspend' : 'activate'} ${confirmDialog.user?.userName || confirmDialog.user?.email}?`}
        confirmText={confirmDialog.action === 'suspend' ? 'Suspend' : 'Activate'}
        confirmColor={confirmDialog.action === 'suspend' ? 'error' : 'primary'}
        loading={updateStatusMutation.isPending}
        onConfirm={handleConfirmStatusToggle}
        onCancel={handleCancelConfirm}
      />

      <Dialog
        open={Boolean(tokenDialogUser)}
        onClose={() => setTokenDialogUser(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgb(30, 41, 59)' : 'white'),
            color: (theme) => (theme.palette.mode === 'dark' ? 'white' : 'rgb(17, 24, 39)'),
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle>Adjust Advance Credits</DialogTitle>
        <DialogContent>
          <div className="space-y-4 pt-2">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {tokenDialogUser?.userName || tokenDialogUser?.email}
              </p>
              <p className="text-sm text-gray-500 dark:text-slate-400">
                Current advance tokens: {formatTokens(tokenDialogUser?.walletTokens ?? 0)}
              </p>
            </div>

            <TextField
              select
              fullWidth
              label="Direction"
              value={tokenForm.direction}
              onChange={(event) =>
                setTokenForm((prev) => ({
                  ...prev,
                  direction: event.target.value as AdjustUserTokenInput['direction'],
                  reason:
                    event.target.value === 'credit'
                      ? prev.reason === 'adjustment'
                        ? 'admin_grant'
                        : prev.reason
                      : 'adjustment',
                }))
              }
            >
              <MenuItem value="credit">Credit</MenuItem>
              <MenuItem value="debit">Debit</MenuItem>
            </TextField>

            <FormattedNumberTextField
              fullWidth
              label="Credit amount"
              value={tokenForm.amount}
              inputProps={{ min: 1, step: 1 }}
              onValueChange={(value) =>
                setTokenForm((prev) => ({
                  ...prev,
                  amount: value ?? 0,
                }))
              }
            />

            <TextField
              select
              fullWidth
              label="Source"
              value={tokenForm.reason}
              onChange={(event) =>
                setTokenForm((prev) => ({
                  ...prev,
                  reason: event.target.value as AdjustUserTokenInput['reason'],
                }))
              }
            >
              <MenuItem value="admin_grant">Admin grant</MenuItem>
              <MenuItem value="refund">Refund</MenuItem>
              <MenuItem value="adjustment">Adjustment</MenuItem>
            </TextField>

            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Note"
              value={tokenForm.note ?? ''}
              onChange={(event) =>
                setTokenForm((prev) => ({
                  ...prev,
                  note: event.target.value,
                }))
              }
            />
          </div>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setTokenDialogUser(null)}
            disabled={adjustTokensMutation.isPending}
            sx={{
              color: (theme) =>
                theme.palette.mode === 'dark' ? 'rgb(148, 163, 184)' : 'rgb(107, 114, 128)',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAdjustTokens}
            disabled={adjustTokensMutation.isPending}
            variant="contained"
            startIcon={adjustTokensMutation.isPending ? <CircularProgress size={16} /> : null}
          >
            {adjustTokensMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
